import { CaptureContext, ScopeContext, User } from "@sentry/core";
import * as Sentry from "@sentry/node";
import { log } from "./logger.js";
import { getRequestContext } from "./requestContext.js";

export class ServerError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ServerError";
  }
}

export class MissingCredentialsError extends ServerError {
  constructor() {
    super(
      "Missing credentials. Please ensure ZEROHEIGHT_CLIENT_ID and ZEROHEIGHT_ACCESS_TOKEN are set in your environment",
    );
  }
}

function withRequestContext(
  context?: CaptureContext,
): CaptureContext | undefined {
  const request = getRequestContext();
  if (!request) return context;

  const tags = {
    request_path: request.path,
    team_id: request.teamId,
  };

  const user = {
    id: request.userId ?? request.viewerId,
    ip_address: request.ip,
  };

  if (!context) return { tags, user };

  const scopeContext = context as Partial<ScopeContext>;
  return {
    ...scopeContext,
    tags: { ...tags, ...(scopeContext.tags ?? {}) },
    user: { ...user, ...(scopeContext.user ?? {}) },
  };
}

export function captureException(exception: unknown) {
  const disableTelemetry = process.env["ZEROHEIGHT_MCP_DISABLE_TELEMETRY"];
  const sentryDSN = process.env["SENTRY_DSN"];
  if (disableTelemetry || !sentryDSN) {
    log.error(exception);
    return;
  }

  Sentry.captureException(exception, withRequestContext());
}

export function captureMessage(message: string, context?: CaptureContext) {
  const disableTelemetry = process.env["ZEROHEIGHT_MCP_DISABLE_TELEMETRY"];
  const sentryDSN = process.env["SENTRY_DSN"];
  if (disableTelemetry || !sentryDSN) {
    log.error(`${message}: ${JSON.stringify(context)}`);
    return;
  }

  Sentry.captureMessage(message, withRequestContext(context));
}
